/**
 * Context-aware background defaults for section insertion.
 *
 * Statement sections (Hero, CTABanner, BookingWidget, StatsBar) get a fixed
 * bold branded background. Content sections alternate between "surface" and
 * "secondaryBg" based on their neighbors at the insertion point, avoiding
 * two adjacent sections with the same background.
 */

/** Statement sections and their fixed branded backgrounds. */
const STATEMENT_SECTIONS: Record<string, string> = {
  Hero: "primaryBg",
  CTABanner: "primary",
  BookingWidget: "primaryBg",
  StatsBar: "primary",
};

/** Alternation cycle for content sections. */
const CONTENT_CYCLE = ["surface", "secondaryBg"] as const;

/**
 * Pick the default `backgroundStyle` for a newly inserted section.
 *
 * @param sectionType  The section family name (e.g. "Hero", "FeatureGrid")
 * @param prevBg       backgroundStyle of the section above the insertion point
 * @param nextBg       backgroundStyle of the section below the insertion point
 */
export function pickDefaultBackground(
  sectionType: string,
  prevBg: string | undefined,
  nextBg: string | undefined,
): string {
  // Statement sections get a fixed bold background
  if (STATEMENT_SECTIONS[sectionType]) {
    return STATEMENT_SECTIONS[sectionType];
  }

  // Content sections alternate between surface and secondaryBg
  for (const bg of CONTENT_CYCLE) {
    if (bg !== prevBg && bg !== nextBg) {
      return bg;
    }
  }
  return "surface"; // fallback when both cycle tones are used by neighbors
}
